// Analytics + Dashboard KPIs.
//
// Every figure = a per-tenant historical baseline (activity from before this browser's data
// existed; zero for a brand-new tenant) PLUS live activity computed from the real conversation and
// lead stores. Starting a visitor chat, escalating it or capturing a lead therefore moves the
// Dashboard and Analytics numbers. In production these are SQL aggregations in the backend.
import { conversationsStore, leadsStore, tenantsStore } from "../core";
import { ANALYTICS, EMPTY_ANALYTICS, LEAD_STATUSES } from "../fixtures";
import { ok, fail } from "../helpers";

const DAY = 24 * 60 * 60 * 1000;
const PLAN_QUOTA = { Starter: { tokens: 1000000, budget: 10000 }, Business: { tokens: 2500000, budget: 25000 }, Enterprise: { tokens: 10000000, budget: 100000 } };

function liveStats(tenantId, fromMs, toMs = Date.now() + 1) {
  const inWindow = (iso) => { const t = new Date(iso).getTime(); return t >= fromMs && t < toMs; };
  const convos = conversationsStore.all().filter((c) => c.tenantId === tenantId && !c.seed && inWindow(c.createdAt));
  const leads = leadsStore.all().filter((l) => l.tenantId === tenantId && !l.seed && inWindow(l.createdAt));
  let messages = 0; let unresolved = 0; const firstResponse = [];
  convos.forEach((c) => {
    messages += c.messages.filter((m) => m.from !== "system").length;
    unresolved += c.messages.filter((m) => m.from === "bot" && m.meta && m.meta.matched === false).length;
    const firstVisitor = c.messages.find((m) => m.from === "visitor");
    const firstAgent = c.messages.find((m) => m.from === "agent");
    if (firstVisitor && firstAgent) firstResponse.push((new Date(firstAgent.createdAt) - new Date(firstVisitor.createdAt)) / 1000);
  });
  const escalated = convos.filter((c) => c.escalatedAt).length;
  return {
    conversations: convos.length, messages, unresolved, escalated, leads: leads.length,
    converted: leads.filter((l) => l.status === "Won").length,
    resolvedByBot: convos.filter((c) => !c.escalatedAt && c.messages.some((m) => m.from === "visitor")).length,
    firstResponse, convos
  };
}

const baselineFor = (tenantId) => ANALYTICS[tenantId] || EMPTY_ANALYTICS;
const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);

/** Builds the full analytics summary for a window of `days`, scaling the baseline by `factor`. */
export function summaryFor(tenantId, days, factor) {
  const base = baselineFor(tenantId);
  const scale = (value) => Math.round(Number(value || 0) * factor);
  const since = Date.now() - days * DAY;
  const live = liveStats(tenantId, since);
  const baseConvos = scale(base.conversationsStarted);

  const totalMessages = scale(base.engagement.totalMessages) + live.messages;
  const totalSessions = scale(base.engagement.totalSessions) + live.conversations;
  const conversationsStarted = baseConvos + live.conversations;
  const captured = scale(base.leadConversion.capturedLeads) + live.leads;
  const converted = scale(base.leadConversion.convertedLeads) + live.converted;
  const allConvos = conversationsStore.all().filter((c) => c.tenantId === tenantId);
  const sampleBase = 10;
  const firstAvg = live.firstResponse.length
    ? Math.round((base.avgFirstResponseSec * sampleBase + live.firstResponse.reduce((a, b) => a + b, 0)) / (sampleBase + live.firstResponse.length))
    : base.avgFirstResponseSec;

  const points = days <= 7 ? 7 : days <= 30 ? 10 : 12;
  const span = (days * DAY) / points;
  const trend = Array.from({ length: points }, (_, i) => {
    const from = since + i * span;
    const added = liveStats(tenantId, from, from + span).conversations;
    return { label: `Day ${i + 1}`, conversations: Math.round((base.conversationsStarted ? 40 + ((i * 17) % 75) : 0) * (days / 7)) + added };
  });

  return {
    openConversations: allConvos.filter((c) => ["Open", "Pending", "Escalated"].includes(c.status)).length,
    resolvedToday: allConvos.filter((c) => ["Closed", "Resolved"].includes(c.status) && Date.now() - new Date(c.updatedAt).getTime() < DAY).length,
    avgFirstResponseSec: firstAvg,
    dateRange: `Last ${days} Days`,
    engagement: { totalMessages, totalSessions, uniqueVisitors: scale(base.engagement.uniqueVisitors) + live.conversations },
    widgetOpens: scale(base.widgetOpens) + live.conversations,
    conversationsStarted,
    messagesPerSession: totalSessions ? Number((totalMessages / totalSessions).toFixed(1)) : 0,
    returningVisitorsRate: base.returningVisitorsRate,
    aiResolutionRate: conversationsStarted ? pct((base.aiResolutionRate / 100) * baseConvos + live.resolvedByBot, conversationsStarted) : 0,
    escalationRate: conversationsStarted ? pct((base.escalationRate / 100) * baseConvos + live.escalated, conversationsStarted) : 0,
    averageResponseTime: base.averageResponseTime,
    unresolvedQuestions: scale(base.unresolvedQuestions) + live.unresolved,
    cta: { ...base.cta, impressions: scale(base.cta.impressions), clicks: scale(base.cta.clicks) },
    leadConversion: { capturedLeads: captured, convertedLeads: converted, conversionRate: pct(converted, captured) },
    trend
  };
}

const analyticsFactor = (days) => (days === 7 ? 1 : days === 30 ? 4.15 : 12.2);
const spark = (value) => [0.78, 0.84, 0.9, 0.94, 0.97, 0.99, 1].map((m) => Math.round(value * m * 10) / 10);
const labelsFor = (range) => (range === "today" ? ["12 AM", "4 AM", "8 AM", "12 PM", "4 PM", "8 PM"] : range === "7d" ? ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"] : ["Wk 1", "Wk 2", "Wk 3", "Wk 4", "Wk 5"]);
const SHAPE = [0.55, 0.7, 0.62, 0.9, 1.0, 0.82, 0.95];
const DASH_DAYS = { today: 1, "7d": 7, "30d": 30 };

function dashboardMetric(key, tenantId, range) {
  const days = DASH_DAYS[range] || 1;
  const s = summaryFor(tenantId, days, days / 7);
  const baseline = baselineFor(tenantId);
  const empty = !baseline.engagement.totalMessages && s.conversationsStarted === 0 && s.leadConversion.capturedLeads === 0;
  const prev = (v) => Math.round(v * 0.9 * 10) / 10;
  const plan = tenantsStore.all().find((t) => t.id === tenantId)?.plan || "Starter";
  const quota = PLAN_QUOTA[plan] || PLAN_QUOTA.Starter;
  const visitors = s.engagement.uniqueVisitors;
  const builders = {
    visitors: () => ({ value: visitors, previous: Math.round(visitors * 0.88), sparkline: spark(visitors) }),
    conversations: () => ({ value: s.conversationsStarted, previous: Math.round(s.conversationsStarted * 0.9), sparkline: spark(s.conversationsStarted) }),
    leads: () => ({ value: s.leadConversion.capturedLeads, previous: Math.round(s.leadConversion.capturedLeads * 1.05), sparkline: spark(s.leadConversion.capturedLeads) }),
    conversionRate: () => {
      const v = visitors ? Math.round((s.leadConversion.capturedLeads / visitors) * 1000) / 10 : 0;
      return { value: v, previous: prev(v), sparkline: spark(v) };
    },
    aiResponse: () => ({ value: s.aiResolutionRate, previous: Math.max(0, s.aiResolutionRate - 2.5), messages: s.engagement.totalMessages, sparkline: spark(s.aiResolutionRate) }),
    escalationRate: () => ({
      value: s.escalationRate, previous: Math.round((s.escalationRate + 2) * 10) / 10,
      escalated: Math.round((s.escalationRate / 100) * s.conversationsStarted), total: s.conversationsStarted, sparkline: spark(s.escalationRate)
    }),
    responseTime: () => ({ value: s.averageResponseTime, previous: s.averageResponseTime + 4, p90: Math.round(s.averageResponseTime * 2.3), unit: "s", sparkline: spark(s.averageResponseTime) }),
    aiUsage: () => {
      const tokensUsed = Math.round(s.engagement.totalMessages * 1450 * (30 / days));
      return { tokensUsed, tokensQuota: quota.tokens, cost: Math.round(tokensUsed * 0.01), budget: quota.budget, currency: "INR", periodLabel: "This month" };
    },
    engagementTrend: () => {
      const labels = range === "today" ? ["12 AM", "4 AM", "8 AM", "12 PM", "4 PM", "8 PM"] : range === "7d" ? ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"] : ["Wk 1", "Wk 2", "Wk 3", "Wk 4", "Wk 5"];
      const n = labels.length;
      const shape = [0.55, 0.7, 0.62, 0.9, 1.0, 0.82, 0.95];
      const per = (total) => labels.map((_, i) => Math.round((total / n) * shape[i % shape.length]));
      return { labels, series: [
        { name: "Visitors", color: "#00D4FF", values: per(visitors) },
        { name: "Conversations", color: "#007BFF", values: per(s.conversationsStarted) }
      ] };
    },
    ctaPerformance: () => {
      const labels = labelsFor(range);
      const spread = (total) => labels.map((_, i) => Math.round((total / labels.length) * SHAPE[i % SHAPE.length]));
      const impressions = spread(s.cta.impressions);
      const clicks = spread(s.cta.clicks);
      const sum = (a) => a.reduce((x, y) => x + y, 0);
      const imp = sum(impressions);
      return {
        labels,
        series: [{ name: "Impressions", color: "#00D4FF", values: impressions }, { name: "Clicks", color: "#007BFF", values: clicks }],
        clickThroughRate: labels.map((_, i) => (impressions[i] ? Math.round((clicks[i] / impressions[i]) * 1000) / 10 : 0)),
        totals: { impressions: imp, clicks: sum(clicks), clickThroughRate: imp ? Math.round((sum(clicks) / imp) * 1000) / 10 : 0 }
      };
    },
    supportResolution: () => ({
      resolved: Math.max(0, s.conversationsStarted - Math.round((s.escalationRate / 100) * s.conversationsStarted)),
      inProgress: s.openConversations, handover: Math.round((s.escalationRate / 100) * s.conversationsStarted)
    })
  };
  if (!builders[key]) return undefined;
  return { data: empty ? null : builders[key](), meta: { range, generatedAt: new Date().toISOString() } };
}


const band = (score) => (score >= 75 ? "Hot" : score >= 40 ? "Warm" : "Cold");

/** Breakdown of the workspace's leads by source, intent, score band and status (all leads, not windowed). */
function leadInsights(tenantId) {
  const leads = leadsStore.all().filter((l) => l.tenantId === tenantId);
  if (!leads.length) return null;
  const total = leads.length;
  const group = (keyFn) => {
    const map = new Map();
    leads.forEach((l) => {
      const key = keyFn(l) || "Unknown";
      const row = map.get(key) || { name: key, count: 0, scoreSum: 0 };
      row.count += 1; row.scoreSum += Number(l.score) || 0;
      map.set(key, row);
    });
    return [...map.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .map((r) => ({ name: r.name, count: r.count, percent: pct(r.count, total), averageScore: Math.round(r.scoreSum / r.count) }));
  };
  const bands = { Hot: { range: "75-100", count: 0 }, Warm: { range: "40-74", count: 0 }, Cold: { range: "0-39", count: 0 } };
  leads.forEach((l) => { bands[band(Number(l.score) || 0)].count += 1; });
  return {
    total,
    averageScore: Math.round(leads.reduce((a, l) => a + (Number(l.score) || 0), 0) / total),
    bySource: group((l) => l.source),
    byIntent: group((l) => l.intent),
    scoreBands: Object.entries(bands).map(([name, v]) => ({ name, range: v.range, count: v.count, percent: pct(v.count, total) })),
    byStatus: LEAD_STATUSES.map((name) => ({ name, count: leads.filter((l) => l.status === name).length }))
  };
}

export const analyticsRoutes = [
  {
    method: "GET", pattern: /^\/api\/analytics\/summary$/, permission: "analytics:read",
    handler: ({ tenantId, scenario, query }) => {
      if (scenario === "empty") return ok(200, null);
      const days = Number(query.get("range")) || 7;
      return ok(200, summaryFor(tenantId, days, analyticsFactor(days)));
    }
  },
  {
    method: "GET", pattern: /^\/api\/analytics\/lead-insights$/, permission: "analytics:read",
    handler: ({ tenantId, scenario }) => ok(200, scenario === "empty" ? null : leadInsights(tenantId))
  },
  {
    method: "GET", pattern: /^\/api\/dashboard\/(?:kpis|charts)\/([\w-]+)$/, permission: "analytics:read",
    handler: ({ tenantId, scenario, query, match }) => {
      const key = match[1].replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      const range = query.get("range") || "today";
      if (!DASH_DAYS[range]) return fail(400, "INVALID_RANGE", `Unsupported range "${range}"`);
      if (scenario === "empty") return ok(200, null, { range });
      const metric = dashboardMetric(key, tenantId, range);
      if (!metric) return fail(404, "NOT_FOUND", `Unknown metric "${key}"`);
      return ok(200, metric.data, metric.meta);
    }
  }
];
